// translations.js — EN/ES translation dictionary + useTranslation hook
// Used by Field Hub, Street Sheet, Utility Hit, and Safety pages
import { useState } from 'react';

export const translations = {
  en: {
    // Hub
    hub_title: 'Field Crew Hub',
    hub_map: 'Map',
    hub_map_desc: 'View segments, devices & pins',
    hub_streetsheet: 'Street Sheet',
    hub_streetsheet_desc: "Submit tonight's report",
    hub_utilityhit: 'Report Utility Hit',
    hub_utilityhit_desc: 'Emergency damage report',
    hub_safety: 'Safety & Procedures',
    hub_safety_desc: 'HSE manual & scope of work',
    hub_deadline: 'Street sheet due by 7:00 PM',
    hub_weekend: 'Weekend sheet — due by 7 PM Friday',
    language: 'Language',

    // Street Sheet
    ss_title: 'Daily Street Sheet',
    ss_project: 'Project',
    ss_project_code: 'Project Code',
    ss_for_date: 'Report For',
    ss_contractor: 'Contractor',
    ss_county: 'County',
    ss_working_tomorrow: 'Working Tomorrow?',
    ss_yes: 'Yes',
    ss_no: 'No',
    ss_reason: 'Reason',
    ss_tomorrows_plan: "Tomorrow's Plan",
    ss_todays_production: "Today's Production",
    ss_crew_leader: 'Crew Leader',
    ss_street_name: 'Street Name',
    ss_cabinet: 'Cabinet',
    ss_activity: 'Activity',
    ss_add_row: 'Add Row',
    ss_remove: 'Remove',
    ss_activity_type: 'Activity Type',
    ss_qty: 'Qty (ft)',
    ss_completed: 'Completed',
    ss_personnel: 'Personnel in Field Today',
    ss_name: 'Name',
    ss_role: 'Role',
    ss_add_person: 'Add Person',
    ss_submit: 'Submit Street Sheet',
    ss_submitting: 'Submitting...',
    ss_deadline: 'Due by 7:00 PM',
    ss_weekend: 'Weekend sheet — covers Sat/Sun/Mon',
    ss_not_working: 'Not Working Tomorrow',
    ss_back: 'Back',
    ss_back_to_hub: 'Back to Hub',
    ss_success: 'Street sheet submitted & emailed to Aspen!',
    ss_subtitle: 'Report what you completed today and your plan for tomorrow',
    ss_select_activity: 'Select activity',
    ss_select_role: 'Select role',
    ss_notes: 'Notes',
    ss_min_one_person: 'At least 1 person required',
    ss_required: 'Required',

    // Utility Hit
    uh_title: 'Utility Strike Report',
    uh_step: 'Step',
    uh_of: 'of',
    uh_emergency: 'Emergency Checklist',
    uh_emergency_desc: 'Complete all safety items before proceeding. Do NOT skip this step.',
    uh_incident: 'Incident Details',
    uh_compliance: 'Compliance & Personnel',
    uh_photos: 'Photos',
    uh_review: 'Review & Submit',
    uh_check_all: 'Check all items before proceeding',
    uh_called_who: 'Who was called? (utility company / 811)',
    uh_date_time: 'Date / Time',
    uh_address: 'Address / Location',
    uh_capture_gps: 'Capture GPS',
    uh_gps_captured: 'GPS Captured',
    uh_utility_type: 'Utility Type',
    uh_utility_owner: 'Utility Owner / Operator',
    uh_severity: 'Severity',
    uh_description: 'Description of Damage',
    uh_marked: 'Was the utility properly marked?',
    uh_locates: 'Were locates called before work?',
    uh_ticket: 'Locate Ticket Number',
    uh_crew_lead: 'Crew Lead Name',
    uh_crew_phone: 'Crew Lead Phone',
    uh_equipment: 'Equipment Used at Time of Damage',
    uh_actions: 'Immediate Actions Taken',
    uh_rep_name: 'Utility Rep on Scene (Name)',
    uh_rep_contact: 'Utility Rep Contact',
    uh_photos_desc: 'At least 1 damage photo is required. Pre-construction and repair photos are strongly recommended.',
    uh_pre_photos: 'Pre-Construction Photos',
    uh_damage_photos: 'Damage Photos',
    uh_repair_photos: 'Repair Photos',
    uh_no_photos: 'No photos yet — tap "Add Photo" to capture or upload',
    uh_add_photo: 'Add Photo',
    uh_preview_pdf: 'Preview PDF',
    uh_submit: 'Submit Report & Email Claims',
    uh_submitted: 'Report Submitted',
    uh_submitted_desc: 'The utility strike report has been saved and emailed to the claims team.',
    uh_view_all: 'View All Reports',
    uh_submit_another: 'Submit Another',
    uh_next: 'Next',
    uh_back: 'Back',
    uh_back_to_hub: 'Back to Hub',
    uh_reported_by: 'Reported by',
    uh_select_type: 'Select type',

    // Emergency checklist items
    uh_checklist_1: 'Area secured / workers clear of hazard',
    uh_checklist_2: '811 / utility company called',
    uh_checklist_3: 'Supervisor notified (Matt or crew lead)',
    uh_checklist_4: 'If gas: evacuate area, no ignition sources',

    // Severity descriptions
    uh_sev_minor: 'Small nick or scratch, no service interruption',
    uh_sev_moderate: 'Visible damage, potential service impact',
    uh_sev_major: 'Line severed or significant damage, service outage',
    uh_sev_critical: 'Gas leak, electrical hazard, or imminent danger',

    // Safety Page
    safety_title: 'Safety & Procedures',
    safety_subtitle: 'Quick reference from the LYT HSE Manual',
    safety_emergency: 'Emergency Contacts',
    safety_download: 'Download Full Manual',
    safety_scope: 'Project Scope of Work',
    safety_ppe: 'PPE Requirements',
    safety_ppe_desc: 'Basic Four: Hard hat, safety glasses, hi-vis vest, steel-toe boots. Gloves as needed for task.',
    safety_trench: 'Trenching & Excavation',
    safety_trench_desc: 'Call 811 before digging. Competent person required on-site. Classify soil type. Slope/shore/shield trenches >5ft.',
    safety_hdd: 'HDD / Boring Safety',
    safety_hdd_desc: 'Complete pre-drill checklist. Verify locates. Manage drilling fluid. Maintain 36" minimum cover.',
    safety_aerial: 'Aerial & Pole Work',
    safety_aerial_desc: 'Fall protection required above 4ft. Climbing qualifications current. Maintain approach distances from power lines.',
    safety_traffic: 'Traffic Control',
    safety_traffic_desc: 'Follow MUTCD standards. Certified flaggers required. Traffic Control Plans (TCPs) must be approved before work.',
    safety_electrical: 'Electrical Safety',
    safety_electrical_desc: 'Lock Out / Tag Out (LOTO) procedures. Maintain approach distances. Only qualified personnel work on energized equipment.',
    safety_fiber: 'Fiber & Laser Safety',
    safety_fiber_desc: 'Never look into fiber ends. Use proper laser safety glasses. Handle fiber carefully — slivers are a puncture hazard.',
    safety_emergency_proc: 'Emergency Procedures',
    safety_emergency_desc: 'Know your emergency contacts. Utility strike: secure area, call 811, notify supervisor. Severe weather: seek shelter immediately.',
    safety_incident: 'Incident Reporting',
    safety_incident_desc: 'Report ALL accidents, injuries, near-misses, and property damage immediately. Near-miss reports prevent future incidents.',
    safety_heat: 'Heat Illness Prevention',
    safety_heat_desc: 'Watch for heat cramps, exhaustion, stroke. Follow 30/30 rule: 30 min work / 30 min rest in extreme heat. Stay hydrated.',
    safety_scope_boring: 'Boring: Min 36" cover, tracer wire required on all runs',
    safety_scope_pulling: 'Pulling: Armored and micro cable in duct',
    safety_scope_fixtures: 'Fixtures: Handholes, flowerpots, pedestals, vaults (pea gravel, ground rod, duct caps)',
    safety_scope_splicing: 'Splicing: Power meter + OTDR testing per strand',
    safety_scope_restoration: 'Restoration: Concrete/asphalt cut & replace',
  },
  es: {
    // Hub
    hub_title: 'Centro de Equipo de Campo',
    hub_map: 'Mapa',
    hub_map_desc: 'Ver segmentos, dispositivos y pines',
    hub_streetsheet: 'Hoja de Calle',
    hub_streetsheet_desc: 'Enviar el reporte de esta noche',
    hub_utilityhit: 'Reportar Golpe de Utilidad',
    hub_utilityhit_desc: 'Reporte de emergencia de dano',
    hub_safety: 'Seguridad y Procedimientos',
    hub_safety_desc: 'Manual HSE y alcance del trabajo',
    hub_deadline: 'Hoja de calle debe entregarse antes de las 7:00 PM',
    hub_weekend: 'Hoja de fin de semana — debe entregarse viernes antes de las 7 PM',
    language: 'Idioma',

    // Street Sheet
    ss_title: 'Hoja de Calle Diaria',
    ss_project: 'Proyecto',
    ss_project_code: 'Codigo de Proyecto',
    ss_for_date: 'Reporte Para',
    ss_contractor: 'Contratista',
    ss_county: 'Condado',
    ss_working_tomorrow: 'Trabajando Manana?',
    ss_yes: 'Si',
    ss_no: 'No',
    ss_reason: 'Razon',
    ss_tomorrows_plan: 'Plan de Manana',
    ss_todays_production: 'Produccion de Hoy',
    ss_crew_leader: 'Lider de Equipo',
    ss_street_name: 'Nombre de Calle',
    ss_cabinet: 'Gabinete',
    ss_activity: 'Actividad',
    ss_add_row: 'Agregar Fila',
    ss_remove: 'Eliminar',
    ss_activity_type: 'Tipo de Actividad',
    ss_qty: 'Cantidad (pies)',
    ss_completed: 'Completado',
    ss_personnel: 'Personal en Campo Hoy',
    ss_name: 'Nombre',
    ss_role: 'Puesto',
    ss_add_person: 'Agregar Persona',
    ss_submit: 'Enviar Hoja de Calle',
    ss_submitting: 'Enviando...',
    ss_deadline: 'Fecha limite: 7:00 PM',
    ss_weekend: 'Hoja de fin de semana — cubre Sab/Dom/Lun',
    ss_not_working: 'No Trabajando Manana',
    ss_back: 'Atras',
    ss_back_to_hub: 'Volver al Centro',
    ss_success: 'Hoja de calle enviada y correo enviado a Aspen!',
    ss_subtitle: 'Reporte lo que completo hoy y su plan para manana',
    ss_select_activity: 'Seleccionar actividad',
    ss_select_role: 'Seleccionar puesto',
    ss_notes: 'Notas',
    ss_min_one_person: 'Se requiere al menos 1 persona',
    ss_required: 'Requerido',

    // Utility Hit
    uh_title: 'Reporte de Golpe de Utilidad',
    uh_step: 'Paso',
    uh_of: 'de',
    uh_emergency: 'Lista de Emergencia',
    uh_emergency_desc: 'Complete todos los puntos de seguridad antes de continuar. NO omita este paso.',
    uh_incident: 'Detalles del Incidente',
    uh_compliance: 'Cumplimiento y Personal',
    uh_photos: 'Fotos',
    uh_review: 'Revisar y Enviar',
    uh_check_all: 'Marque todos los puntos antes de continuar',
    uh_called_who: 'A quien se llamo? (compania de servicios / 811)',
    uh_date_time: 'Fecha / Hora',
    uh_address: 'Direccion / Ubicacion',
    uh_capture_gps: 'Capturar GPS',
    uh_gps_captured: 'GPS Capturado',
    uh_utility_type: 'Tipo de Utilidad',
    uh_utility_owner: 'Propietario / Operador de Utilidad',
    uh_severity: 'Severidad',
    uh_description: 'Descripcion del Dano',
    uh_marked: 'Estaba la utilidad correctamente marcada?',
    uh_locates: 'Se llamaron localizaciones antes del trabajo?',
    uh_ticket: 'Numero de Ticket de Localizacion',
    uh_crew_lead: 'Nombre del Lider de Equipo',
    uh_crew_phone: 'Telefono del Lider de Equipo',
    uh_equipment: 'Equipo Usado al Momento del Dano',
    uh_actions: 'Acciones Inmediatas Tomadas',
    uh_rep_name: 'Representante de Utilidad en Escena (Nombre)',
    uh_rep_contact: 'Contacto del Representante',
    uh_photos_desc: 'Se requiere al menos 1 foto de dano. Fotos de pre-construccion y reparacion son recomendadas.',
    uh_pre_photos: 'Fotos de Pre-Construccion',
    uh_damage_photos: 'Fotos de Dano',
    uh_repair_photos: 'Fotos de Reparacion',
    uh_no_photos: 'Sin fotos aun — toque "Agregar Foto" para capturar o subir',
    uh_add_photo: 'Agregar Foto',
    uh_preview_pdf: 'Vista Previa PDF',
    uh_submit: 'Enviar Reporte y Correo a Reclamos',
    uh_submitted: 'Reporte Enviado',
    uh_submitted_desc: 'El reporte de golpe ha sido guardado y enviado al equipo de reclamos.',
    uh_view_all: 'Ver Todos los Reportes',
    uh_submit_another: 'Enviar Otro',
    uh_next: 'Siguiente',
    uh_back: 'Atras',
    uh_back_to_hub: 'Volver al Centro',
    uh_reported_by: 'Reportado por',
    uh_select_type: 'Seleccionar tipo',

    // Emergency checklist items
    uh_checklist_1: 'Area asegurada / trabajadores fuera de peligro',
    uh_checklist_2: '811 / compania de servicios llamada',
    uh_checklist_3: 'Supervisor notificado (Matt o lider de equipo)',
    uh_checklist_4: 'Si es gas: evacuar area, sin fuentes de ignicion',

    // Severity descriptions
    uh_sev_minor: 'Raspon pequeno, sin interrupcion de servicio',
    uh_sev_moderate: 'Dano visible, posible impacto en servicio',
    uh_sev_major: 'Linea cortada o dano significativo, interrupcion de servicio',
    uh_sev_critical: 'Fuga de gas, peligro electrico, o peligro inminente',

    // Safety Page
    safety_title: 'Seguridad y Procedimientos',
    safety_subtitle: 'Referencia rapida del Manual HSE de LYT',
    safety_emergency: 'Contactos de Emergencia',
    safety_download: 'Descargar Manual Completo',
    safety_scope: 'Alcance del Trabajo del Proyecto',
    safety_ppe: 'Requisitos de EPP',
    safety_ppe_desc: 'Los Cuatro Basicos: Casco, lentes de seguridad, chaleco de alta visibilidad, botas con punta de acero. Guantes segun la tarea.',
    safety_trench: 'Zanjas y Excavacion',
    safety_trench_desc: 'Llame al 811 antes de excavar. Persona competente requerida en sitio. Clasificar tipo de suelo. Talud/apuntalar zanjas >5 pies.',
    safety_hdd: 'Seguridad HDD / Perforacion',
    safety_hdd_desc: 'Completar lista de verificacion pre-perforacion. Verificar localizaciones. Manejar fluido de perforacion. Mantener 36" minimo de cobertura.',
    safety_aerial: 'Trabajo Aereo y en Postes',
    safety_aerial_desc: 'Proteccion contra caidas requerida arriba de 4 pies. Calificaciones de escalada vigentes. Mantener distancias de acercamiento a lineas electricas.',
    safety_traffic: 'Control de Trafico',
    safety_traffic_desc: 'Seguir estandares MUTCD. Se requieren senaladores certificados. Planes de Control de Trafico (TCPs) deben ser aprobados antes del trabajo.',
    safety_electrical: 'Seguridad Electrica',
    safety_electrical_desc: 'Procedimientos de Bloqueo/Etiquetado (LOTO). Mantener distancias de acercamiento. Solo personal calificado trabaja en equipo energizado.',
    safety_fiber: 'Seguridad de Fibra y Laser',
    safety_fiber_desc: 'Nunca mire los extremos de la fibra. Use lentes de seguridad laser apropiados. Maneje la fibra con cuidado — las astillas son peligro de puncion.',
    safety_emergency_proc: 'Procedimientos de Emergencia',
    safety_emergency_desc: 'Conozca sus contactos de emergencia. Golpe de utilidad: asegurar area, llamar 811, notificar supervisor. Clima severo: buscar refugio inmediatamente.',
    safety_incident: 'Reporte de Incidentes',
    safety_incident_desc: 'Reporte TODOS los accidentes, lesiones, casi-accidentes y danos a propiedad inmediatamente. Reportes de casi-accidentes previenen incidentes futuros.',
    safety_heat: 'Prevencion de Enfermedades por Calor',
    safety_heat_desc: 'Busque calambres, agotamiento, golpe de calor. Siga regla 30/30: 30 min trabajo / 30 min descanso en calor extremo. Mantengase hidratado.',
    safety_scope_boring: 'Perforacion: Min 36" de cobertura, cable trazador requerido en todos los tramos',
    safety_scope_pulling: 'Jalado: Cable blindado y micro en ducto',
    safety_scope_fixtures: 'Accesorios: Registros, macetas, pedestales, bovedas (grava, varilla de tierra, tapas de ducto)',
    safety_scope_splicing: 'Empalmado: Medidor de potencia + prueba OTDR por hebra',
    safety_scope_restoration: 'Restauracion: Corte y reemplazo de concreto/asfalto',
  },
};

export function useTranslation() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('lyt-lang') || 'en'; }
    catch { return 'en'; }
  });

  const t = (key) => translations[lang]?.[key] || translations.en[key] || key;

  const toggleLang = () => {
    const next = lang === 'en' ? 'es' : 'en';
    try { localStorage.setItem('lyt-lang', next); } catch {}
    setLang(next);
  };

  return { t, lang, setLang, toggleLang };
}
